import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useApi, useFetch, errMsg } from "../lib/api";
import { PageHeader, Empty } from "../components/shared";
import { FormDialog } from "../components/FormDialog";
import { Button } from "../components/ui/button";

const FIELDS = [
  { name: "company_name", label: "Company name", required: true },
  { name: "cin_number", label: "CIN (21 characters)", required: true, minLength: 21, maxLength: 21, placeholder: "U27100MH2011PTC215478" },
  { name: "pan_number", label: "PAN (10 characters)", required: true, minLength: 10, maxLength: 10, placeholder: "AAECA4821K" },
  { name: "registered_office_address", label: "Registered office address", required: true },
];

export default function Employers() {
  const api = useApi();
  const { data, reload } = useFetch("/employers");
  const [edit, setEdit] = useState(null);

  const save = async (form) => {
    try {
      if (edit.id) await api.put(`/employers/${edit.id}`, form); else await api.post("/employers", form);
      toast.success("Employer saved"); reload(); return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const remove = async (e) => {
    if (!window.confirm(`Delete ${e.company_name} and all its vendors, workers and records?`)) return;
    try { await api.delete(`/employers/${e.id}`); toast.success("Deleted"); reload(); } catch (er) { toast.error(errMsg(er)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Enterprise clients" title="Principal employers" desc="Companies engaging contract labour through vendor partners.">
        <Button onClick={() => setEdit({})} data-testid="employer-add-button" className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold">
          <Plus size={15} className="mr-1.5" />Add employer
        </Button>
      </PageHeader>
      <div className="c-surface overflow-x-auto c-rise">
        <table className="c-table w-full">
          <thead><tr><th>Company</th><th>CIN</th><th>PAN</th><th>Registered office</th><th>Vendors</th><th /></tr></thead>
          <tbody>
            {(data || []).map((e) => (
              <tr key={e.id} data-testid="employer-table-row">
                <td className="font-medium">{e.company_name}</td>
                <td className="font-mono text-xs text-slate-300">{e.cin_number}</td>
                <td className="font-mono text-xs">{e.pan_number}</td>
                <td className="text-slate-400 max-w-xs truncate">{e.registered_office_address}</td>
                <td className="font-mono">{e.vendor_count}</td>
                <td className="text-right">
                  <button className="p-1.5 text-slate-400 hover:text-white" onClick={() => setEdit(e)} data-testid="employer-edit-btn"><Pencil size={14} /></button>
                  <button className="p-1.5 text-slate-400 hover:text-rose-400" onClick={() => remove(e)} data-testid="employer-delete-btn"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.length === 0 && <Empty text="No principal employers yet. Add one, or load sample data from the dashboard." />}
      </div>
      {edit && <FormDialog open onOpenChange={() => setEdit(null)} title={edit.id ? "Edit employer" : "Add principal employer"}
        fields={FIELDS} initial={edit} onSubmit={save} testid="employer-form" />}
    </div>
  );
}
